import React, { useEffect, useState } from 'react';
import { X, ChevronRight, Bell, Star, Gift, Heart, MessageCircle, Trophy, Zap, Flame, Crown } from 'lucide-react';
import { cn } from '../lib/utils';
interface AlertBannerProps {
  type: 'premium' | 'reward' | 'fanclub' | 'behavioral' | 'feature';
  title: string;
  message: string;
  cta: string;
  action?: string;
  avatar?: string;
  onClose: () => void;
  onAction: () => void;
  displayDuration?: number; // Added configurable duration
}
export const AlertBanner: React.FC<AlertBannerProps> = ({
  type,
  title,
  message,
  cta,
  avatar,
  onClose,
  onAction,
  displayDuration = 15000 // Default to 15 seconds instead of 8
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 100);
    // Auto-dismiss after displayDuration
    const timer = setTimeout(() => {
      handleClose();
    }, displayDuration);
    return () => clearTimeout(timer);
  }, [displayDuration]);
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };
  const getIcon = () => {
    switch (type) {
      case 'premium':
        return <Flame size={20} className="text-purple-500" data-id="element-1242" />;
      case 'reward':
        return <Star size={20} className="text-yellow-500" data-id="element-1243" />;
      case 'fanclub':
        return <Crown size={20} className="text-pink-500" data-id="element-1244" />;
      case 'behavioral':
        return <Heart size={20} className="text-pink-500" data-id="element-1245" />;
      case 'feature':
        return <Zap size={20} className="text-teal-500" data-id="element-1246" />;
      default:
        return <Bell size={20} className="text-gray-500" data-id="element-1247" />;
    }
  };
  const getBgColor = () => {
    switch (type) {
      case 'premium':
        return 'bg-gradient-to-r from-purple-500/90 to-indigo-500/90 dark:from-purple-900/90 dark:to-indigo-900/90 border-purple-200 dark:border-purple-800/30';
      case 'reward':
        return 'bg-gradient-to-r from-yellow-500/90 to-amber-500/90 dark:from-yellow-900/90 dark:to-amber-900/90 border-yellow-200 dark:border-yellow-800/30';
      case 'fanclub':
        return 'bg-gradient-to-r from-pink-500/90 to-rose-500/90 dark:from-pink-900/90 dark:to-rose-900/90 border-pink-200 dark:border-pink-800/30';
      case 'behavioral':
        return 'bg-gradient-to-r from-pink-500/90 to-purple-500/90 dark:from-pink-900/90 dark:to-purple-900/90 border-pink-200 dark:border-pink-800/30';
      case 'feature':
        return 'bg-gradient-to-r from-teal-500/90 to-cyan-500/90 dark:from-teal-900/90 dark:to-cyan-900/90 border-teal-200 dark:border-teal-800/30';
      default:
        return 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    }
  };
  const getButtonColor = () => {
    switch (type) {
      case 'premium':
        return 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-purple-500/20';
      case 'reward':
        return 'bg-gradient-to-r from-yellow-600 to-amber-600 hover:shadow-yellow-500/20';
      case 'fanclub':
        return 'bg-gradient-to-r from-pink-600 to-rose-600 hover:shadow-pink-500/20';
      case 'behavioral':
        return 'bg-gradient-to-r from-pink-600 to-purple-600 hover:shadow-pink-500/20';
      case 'feature':
        return 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:shadow-teal-500/20';
      default:
        return 'bg-gray-800 hover:bg-gray-700 hover:shadow-gray-500/20';
    }
  };
  return <div className={cn('fixed top-80 left-4 z-50 max-w-xs w-full shadow-lg rounded-lg border overflow-hidden transition-all duration-300 transform', getBgColor(), isVisible && !isExiting ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0')} data-id="element-1248">
      <div className="p-3 flex gap-2 items-start" data-id="element-1249">
        {avatar ? <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border-2 border-white dark:border-gray-800 shadow-sm" data-id="element-1250">
            <img src={avatar} alt="User avatar" className="w-full h-full object-cover" data-id="element-1251" />
          </div> : <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-700 flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-1252">
            {getIcon()}
          </div>}
        <div className="flex-1 min-w-0" data-id="element-1253">
          <div className="flex justify-between items-start" data-id="element-1254">
            <h4 className="text-sm font-medium text-white dark:text-white" data-id="element-1255">
              {title}
            </h4>
            <button onClick={handleClose} className="ml-2 text-white/70 dark:text-white/70 hover:text-white dark:hover:text-white flex-shrink-0" data-id="element-1256">
              <X size={14} data-id="element-1257" />
            </button>
          </div>
          <p className="text-xs text-white/90 dark:text-white/90 mt-1" data-id="element-1258">
            {message}
          </p>
          <div className="mt-2" data-id="element-1259">
            <button onClick={() => {
            onAction();
            handleClose();
          }} className={cn('text-xs font-medium px-3 py-1 text-white rounded-full hover:shadow-md transition-all', getButtonColor())} data-id="element-1260">
              {cta}
            </button>
          </div>
        </div>
      </div>
      <div className="h-1 bg-gray-200/30 dark:bg-gray-700/30 w-full overflow-hidden" data-id="element-1261">
        <div className="h-full bg-white/70" style={{
        width: '100%',
        animation: `shrink ${displayDuration / 1000}s linear forwards`
      }} data-id="element-1262" />
      </div>
      <style jsx data-id="element-1263">{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>;
};